<template>
    <section class="content w-full flex flex-col pt-16 justify-center pb-10">
        <div class="w-full border-y-2 border-carrot py-10">
            <div class="px-5 w-full md:w-2/3 mx-auto rounded">
                <div v-if="course.loading" class="text-center">
                    <LoaderComponent />
                </div>
                
                <section v-else id="course-info-section" class="p-3 rounded flex flex-wrap md:flex-nowrap">
                    <div id="course-info" class="w-full md:w-2/3 bg-light-white border border-light-white shadow-lg rounded px-2">
                        <CourseDescriptionComponent 
                            :course-data="course.data"
                            :module-data="modulesData"
                            :category-data="course.categories"
                            :user-lang="store.getters.getUserLang"
                            :instructor="course.instructor"
                        />
                    </div>

                    <div id="buy-card" class="w-full md:w-1/3 ml-0 md:ml-5 mt-5 md:mt-0">
                        <div class="mx-auto py-10 text-center bg-light-white border border-light-white shadow-lg rounded">
                            <div v-if="course.loading">
                                <LoaderComponent />
                            </div>
                            <div v-if="!course.loading" class="w-full px-3">
                                <template v-if="!isMyCourse">
                                    <p class="w-full text-left mb-5 text-xl font-bold">
                                        R${{ course.data.price }}
                                    </p>

                                    <Button
                                        label="Comprar curso"
                                        class="no-bg-button w-full"
                                        icon="pi pi-money-bill"
                                        @click.prevent="goToPurchaseCoursePage(course.data)"
                                    />
                                </template>

                                <div class="text-center" v-else>
                                    <small class="w-full text-xl font-bold text-blog-red">
                                        {{ translate('Course already purchased') }}
                                    </small>
                                    <Button
                                        label="Assistir curso"
                                        class="no-bg-button w-full mt-2"
                                        icon="pi pi-book"
                                        @click.prevent="goToCourseView(course.data)"
                                    />
                                </div>

                                <p class="text-secondary text-left text-lg font-bold mt-3 pt-3 border-t border-iron">
                                    {{ translate('This course includes:') }}
                                </p>
                                <div class="text-left text-sm text-gray-600 layout-bullets">
                                    <p class="my-2 flex items-center gap-2">
                                        <i class="pi pi-desktop"></i> {{ minutesToTime(course.data.timeInMinutes, true) }} {{ translate('of video lessons') }}
                                    </p>
                                    <p class="my-2 flex items-center gap-2">
                                        <i class="pi pi-file"></i> {{ course.data.resoursesCount }} {{ translate('available resources') }}
                                    </p>
                                    <p class="my-2 flex items-center gap-2">
                                        <i class="pi pi-mobile"></i> {{ translate('Watch on your mobile phone or computer') }}
                                    </p>
                                    <p class="my-2 flex items-center gap-2">
                                        <i class="pi pi-history"></i> {{ translate('1-year access') }}
                                    </p>
                                    <p class="my-2 flex items-center gap-2">
                                        <i class="pi pi-trophy"></i> {{ translate('certificate of completion', 'ucfirst') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </section>
</template>

<script setup>
import LoaderComponent from '@/components/Utils/LoaderComponent.vue';
import CourseDescriptionComponent from '@/components/Course/CourseDescriptionComponent.vue';
import requestHandler from '@/composables/requestHandler';
import { ucfirst } from '@/composables/textUtils';
import { showToast } from '@/composables/toast';
import utils from '@/composables/utils';
import CourseService from '@/services/CourseService';
import store from '@/store';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { minutesToTime } from '@/composables/locales';
import { getBase64 } from '@/composables/imageChecker';
import UseStoreHandler from '@/composables/handlers/UseStoreHandler';
import InstructorService from '@/services/InstructorService';
import CategoryService from '@/services/CategoryService';
import urlHandler from '@/composables/urlHandler';
import { translate } from '@/composables/translate';

const toast = useToast();
const route = useRoute();
const thisUuid = ref(null);
const authStore = UseStoreHandler.getAuthStore();
const storageStore = UseStoreHandler.getStoreContext();

onMounted(() => {
    if ([null, '', undefined].includes(route.params) || [null, '', undefined].includes(route.params.uuid))
        return goToHome();

    thisUuid.value = route.params.uuid;
    getCourseInfo();
});

watch(
    () => route.params.uuid,
    (newUuid) => {
        thisUuid.value = newUuid;
        getCourseInfo();
    }
);

let course = reactive({
    data: {},
    loading: true,
    modules: [],
    loadingModules: true,
    instructor: null,
    categories: []
})

async function getCourseInfo() {
    course.loading = true;
    if ([null, '', undefined].includes(route.params) || [null, '', undefined].includes(route.params.uuid))
        return goToHome();
    let courseCachedData = store.getters.getOnDataChest(`course${route.params.uuid}`);
    let courseObject = {};
    if (!utils.isEmpty(courseCachedData)) {
        courseObject = courseCachedData;
    } else {
        const response = await CourseService.showCourse(route.params.uuid);
        if (!response.success())
            return goToHome();
        courseObject = response.getContent();
        courseObject.coursePhoto = await getBase64(courseObject.coursePhoto, true);
        
        if (typeof courseObject.getCoursePhoto !== 'function') {
            courseObject.getCoursePhoto = function() { return this.coursePhoto; };
        }

        store.dispatch('saveOnDataChest', {
            key: `course${route.params.uuid}`,
            value: courseObject
        });
    }
    course.data = courseObject;
    course.categories = await CategoryService.getByCourse(courseObject.uuid);

    course.loading = false;

    getCouseModules();
    canBuyCourse();

    course.instructor = await InstructorService.getDefaultInstructor();
}

async function getCouseModules() {
    course.loadingModules = true;
    course.modules = [];
    let courseModuleCachedData = store.getters.getOnDataChest(`simplified_course_modules_${route.params.uuid}`);
    if (!utils.isEmpty(courseModuleCachedData)) {
        course.modules = courseModuleCachedData;
    } else {
        const response = await CourseService.getSimplifiedCourseModules(route.params.uuid);
        if (!response.success())
            return;
        course.modules = response.getContent();
        store.dispatch('saveOnDataChest', {
            key: `simplified_course_modules_${route.params.uuid}`,
            value: response.getContent()
        })
    }

    processModulesData();
}

let modulesData = ref();
function processModulesData() {
    let tableObj = [];
    let index = 1;
    for (let module of course.modules) {
        let children = [];
        let childrenIndex = 1;
        for (let video of module.getVideos()) {
            children.push({
                "key": `${index}.${childrenIndex}`,
                "data": {
                    'title': `${index}.${childrenIndex} ${ucfirst(video.description)}`,
                    'time': minutesToTime(video.timeInMinutes, true)
                }
            });
            childrenIndex++;
        }

        let thisOption = {
            "key": `${index}`,
            "data": {
                'title': `${index}. ${ucfirst(module.title)}`,
                'time': minutesToTime(module.timeInMinutes, true)
            },
            "children": children
        };
        index++;
        tableObj.push(thisOption);
    }

    modulesData.value = tableObj;
    course.loadingModules = false;
}

async function goToHome() {
    await requestHandler.redirect('/');
    return false;
}

async function goToPurchaseCoursePage() {
    course.loading = false;
    if (!authStore.isAuthenticated) {
        store.dispatch('saveSessionMessage', { message: translate('Not logged in'), messageType: 'error' });
        await requestHandler.redirect(`/`);
    }

    storageStore.save('PURCHASE_ITEM', [course.data]);
    await requestHandler.redirect(`/purchase`);

    return false;
}

function goToCourseView(courseData = {}) {
    if (!courseData || courseData.uuid == undefined) {
        showToast(toast, translate('course not foud', 'ucfirst'), 'info');
        return;
    }

    urlHandler.redirectAndScroll(`/dashboard/course/${courseData.uuid}`);
}

const isMyCourse = ref(false);
async function canBuyCourse() {
    if (!authStore.isAuthenticated) {
        isMyCourse.value = false;
        return;
    }

    isMyCourse.value = await CourseService.isMyCourse(course.data.uuid);
}
</script>